/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Mentorship tab (C4). Consumes GET /api/network/mentorship.
 *
 * Two panels:
 *   1. Learning opportunities — "N contacts in your network could
 *      mentor you in WMS" (from findMentorshipOpportunities)
 *   2. Skills the user could mentor in — skill list only, no counts
 *      (from findMentees, rewritten 2026-08-30 — no honest person
 *      count exists on this side under the negation filter, so the
 *      panel shows discoverable skills without a fabricated number)
 *
 * S6: no names anywhere. Descriptors are pre-computed server-side by
 * describeConnection().
 *
 * Empty states must explain WHY they're empty (no bare zeros).
 */

import React from 'react';

export default function NetworkMentorship({ mentorship }) {
  if (!mentorship) {
    return <div style={emptyStyle}>Mentorship data not available yet.</div>;
  }

  const {
    mentorshipScore = 0,
    learningOpportunities = { total: 0, recommendations: [] },
    mentoringOpportunities = { total: 0, recommendations: [] }
  } = mentorship;

  const hasLearning = (learningOpportunities.recommendations || []).length > 0;
  const hasMentoring = (mentoringOpportunities.recommendations || []).length > 0;

  return (
    <div>
      <div style={scoreCardStyle}>
        <div style={{ fontSize: '32px', fontWeight: 700, color: '#00BCD4' }}>
          {mentorshipScore}<span style={{ fontSize: '18px', color: '#64748b' }}>/100</span>
        </div>
        <div style={{ fontSize: '13px', color: '#64748b' }}>Mentorship score</div>
      </div>

      {/* Learning opportunities */}
      <div style={panelStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Who could mentor you</h3>
        {hasLearning ? (
          <ul style={listStyle}>
            {learningOpportunities.recommendations.map((opp, i) => (
              <li key={i} style={listItemStyle}>
                <div style={{ fontWeight: 600 }}>{opp.skill}</div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  {opp.mentorsAvailable} contact{opp.mentorsAvailable === 1 ? '' : 's'} in your network could mentor you
                </div>
                {opp.sampleMentors && opp.sampleMentors.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    e.g. {opp.sampleMentors.slice(0, 3).join('; ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div style={emptyReasonStyle}>
            No learning opportunities identified yet.
            {learningOpportunities.total === 0 && (
              <div style={{ marginTop: '8px', fontSize: '13px' }}>
                Import your network CSV to see which contacts in your network could mentor you in specific skills.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mentoring opportunities */}
      <div style={panelStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Skills you could mentor in</h3>
        {hasMentoring ? (
          <ul style={listStyle}>
            {mentoringOpportunities.recommendations.map((opp, i) => (
              <li key={i} style={listItemStyle}>
                <div style={{ fontWeight: 600 }}>{opp.skill}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div style={emptyReasonStyle}>
            No mentoring opportunities identified yet.
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              Add skills to your profile so we can show you contacts who could benefit from your expertise.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const scoreCardStyle = {
  background: '#f8fafc',
  padding: '20px',
  borderRadius: '8px',
  textAlign: 'center',
  marginBottom: '20px'
};

const panelStyle = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px'
};

const listStyle = {
  listStyle: 'none',
  padding: 0,
  margin: 0
};

const listItemStyle = {
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9'
};

const emptyStyle = {
  padding: '40px 20px',
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: '14px'
};

const emptyReasonStyle = {
  padding: '20px',
  background: '#f8fafc',
  borderRadius: '6px',
  color: '#475569',
  fontSize: '14px'
};
